// An offer with more than one kind of appointment in it, shown as steps.
//
// The Wellness exam and Pre-op buttons put a lab draw and the doctor's visit
// in one offer, and the panel showed them as a flat list with no way to
// tell which was which, nor how to add a time for the other half (switch
// the dropdown — nobody guessed). The offer is grouped by service and
// provider now, each step with its own times and its own "add a time".
//
// The rule that built a set is not checked on times staff add by hand, and
// the patient page checks only that two chosen times do not overlap. So the
// office is warned here, about the worst pair a patient could pick — a
// warning, not a block: the office may know something the rule does not.

interface StepSlot {
  date?: string;
  time?: string;
  duration?: number;
  serviceId?: string;
  specialistId?: string;
  serviceType?: string;
  specialist?: string;
}

export interface OfferItem { key: string; slot: StepSlot; }

export interface OfferStep {
  serviceId: string;
  specialistId: string;
  title: string;
  firstDate: string;
  items: OfferItem[];
}

export interface BundleStep {
  stepOrder: number;
  serviceId: string;
  specialistId?: string | null;
  offsetDays: number;
  sameDay: boolean;
}

export interface BundleRule { key: string; name: string; steps: BundleStep[]; }

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const clean = (v?: string | null): string => String(v || '').trim();

/** The date's parts, read from its digits — never parsed as UTC. */
const parts = (date?: string): [number, number, number] | null => {
  const m = String(date || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};

const dayNumber = (date?: string): number | null => {
  const p = parts(date);
  return p ? Math.round(Date.UTC(p[0], p[1] - 1, p[2]) / 86400000) : null;
};

/** "Tue, Oct 13". */
export function dayLabel(date?: string): string {
  const p = parts(date);
  if (!p) return clean(date) || 'a date';
  return `${WEEKDAYS[new Date(p[0], p[1] - 1, p[2]).getDay()]}, ${MONTHS[p[1] - 1]} ${p[2]}`;
}

const minutesOf = (time?: string): number => {
  const m = String(time || '').match(/^(\d{1,2}):(\d{2})/);
  return m ? Number(m[1]) * 60 + Number(m[2]) : 0;
};

/** The offer grouped by service and provider, in the order each first appears. */
export function offerSteps(items: OfferItem[]): OfferStep[] {
  const out: OfferStep[] = [];
  items.forEach((item) => {
    const serviceId = clean(item.slot.serviceId);
    const specialistId = clean(item.slot.specialistId);
    let step = out.find((s) => s.serviceId === serviceId && s.specialistId === specialistId);
    if (!step) {
      const service = clean(item.slot.serviceType) || 'Appointment';
      const provider = clean(item.slot.specialist);
      step = {
        serviceId, specialistId,
        title: provider ? `${service} with ${provider}` : service,
        firstDate: clean(item.slot.date),
        items: [],
      };
      out.push(step);
    }
    step.items.push(item);
    if (step.firstDate === '' || (clean(item.slot.date) && clean(item.slot.date) < step.firstDate)) {
      step.firstDate = clean(item.slot.date);
    }
  });
  return out;
}

/** Where to ask for another set from: the day after the earliest date proposed. */
export function nextProposalFrom(steps: Array<{ date?: string }>): string {
  const earliest = steps.map((s) => clean(s.date)).filter(Boolean).sort()[0];
  const p = parts(earliest);
  if (!p) return '';
  const next = new Date(p[0], p[1] - 1, p[2] + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
}

const matches = (slot: StepSlot, step: BundleStep): boolean =>
  clean(slot.serviceId) === clean(step.serviceId)
  && (!clean(step.specialistId) || clean(slot.specialistId) === clean(step.specialistId));

/**
 * One sentence per rule the offer could break, about the worst pair a
 * patient could pick. Only rules whose every step is in the offer count.
 */
export function bundleWarnings(items: OfferItem[], rules: BundleRule[]): string[] {
  const out: string[] = [];
  rules.forEach((rule) => {
    const ordered = [...rule.steps].sort((a, b) => a.stepOrder - b.stepOrder);
    if (ordered.length < 2) return;
    const first = ordered[0];
    const second = ordered[1];
    const firsts = items.map((i) => i.slot).filter((s) => matches(s, first));
    const seconds = items.map((i) => i.slot).filter((s) => matches(s, second));
    if (!firsts.length || !seconds.length) return;
    const nameOf = (slots: StepSlot[]) => clean(slots[0].serviceType) || 'appointment';
    const firstName = nameOf(firsts);
    const secondName = nameOf(seconds);

    if (second.sameDay) {
      const endsBefore = (a: StepSlot, b: StepSlot) => minutesOf(b.time) >= minutesOf(a.time) + (a.duration || 0);
      const lonelyFirst = firsts.find((a) => !seconds.some((b) => clean(b.date) === clean(a.date) && endsBefore(a, b)));
      const lonelySecond = seconds.find((b) => !firsts.some((a) => clean(a.date) === clean(b.date) && endsBefore(a, b)));
      if (!lonelyFirst && !lonelySecond) return;
      const halves = [
        lonelyFirst ? `The ${firstName} on ${dayLabel(lonelyFirst.date)} has no ${secondName} later that day` : '',
        lonelySecond ? `the ${secondName} on ${dayLabel(lonelySecond.date)} has no ${firstName} earlier that day` : '',
      ].filter(Boolean);
      const joined = halves.length === 2 ? `${halves[0]}, and ${halves[1]}` : halves[0].replace(/^the /, 'The ');
      out.push(`A ${rule.name} is the labs and then the visit on one morning. ${joined}.`);
      return;
    }

    const gap = Number(second.offsetDays || 0);
    if (gap <= 0) return;
    // The worst pair: the latest first-step date against the earliest second.
    const latestFirst = [...firsts].sort((a, b) => clean(b.date).localeCompare(clean(a.date)))[0];
    const earliestSecond = [...seconds].sort((a, b) => clean(a.date).localeCompare(clean(b.date)))[0];
    const a = dayNumber(latestFirst.date);
    const b = dayNumber(earliestSecond.date);
    if (a === null || b === null) return;
    const apart = b - a;
    if (apart >= gap) return;
    const howFar = apart < 0 ? 'the visit before the labs' : `${apart} day${apart === 1 ? '' : 's'} apart`;
    out.push(
      `A patient could choose the ${firstName} on ${dayLabel(latestFirst.date)} and the ${secondName} on ${dayLabel(earliestSecond.date)} — ${howFar}. `
      + `A ${rule.name} needs at least ${gap} days between them. Remove one of those, or add later ${secondName} times.`,
    );
  });
  return out;
}

/**
 * What the picker says about where a time it adds will go, when the offer
 * has steps and the dropdown matches one. "Add a time for this step" moves
 * the dropdown, which sits above the offer, out of sight of the button — this
 * line is how the click is seen to have worked. Null when there is nothing
 * to say.
 */
export function pickingForStep(
  groups: OfferStep[],
  mapping: { service?: { id?: string } | null; specialist?: { id?: string } | null } | null,
): string | null {
  if (groups.length < 2 || !mapping) return null;
  const index = groups.findIndex((g) => g.serviceId === clean(mapping.service?.id) && g.specialistId === clean(mapping.specialist?.id));
  if (index < 0) return null;
  return `Times you add now go to step ${index + 1} — ${groups[index].title}.`;
}
